/**
 * Framework catalogue.
 *
 * Shows which regulations are licensed, what each costs, and — importantly —
 * the citation for the source text, so a user can verify our interpretation
 * against the original law rather than taking it on trust.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { Card, Button, Pill, Modal, Skeleton, ErrorNotice } from '../components/ui';

interface Framework {
  id: string; key: string; name: string; version: string; description: string;
  sourceCitation: string | null; sourceUrl: string | null;
  licenseCost: number; questionCount: number;
  licensed: boolean; licenseExpiresAt: string | null;
}

export function FrameworksPage() {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [purchasing, setPurchasing] = useState<Framework | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: frameworks, isLoading } = useQuery({
    queryKey: ['frameworks'],
    queryFn: () => api.get<Framework[]>('/frameworks'),
  });

  const { data: pricing } = useQuery({
    queryKey: ['credits', 'balance'],
    queryFn: () => api.get<{ balance: number }>('/credits/balance'),
    enabled: allows('credit.read'),
  });

  const purchase = useMutation({
    mutationFn: (key: string) => api.post(`/frameworks/${key}/license`),
    onSuccess: () => {
      setPurchasing(null);
      setError(null);
      // Both the catalogue and the balance changed.
      void queryClient.invalidateQueries({ queryKey: ['frameworks'] });
      void queryClient.invalidateQueries({ queryKey: ['credits'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err) => setError(describeError(err)),
  });

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));

  if (isLoading) {
    return (
      <div className="oc-page space-y-4">
        <Skeleton className="h-9 w-48" />
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-44" />)}
      </div>
    );
  }

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{t('frameworks.title')}</h1>
        <p className="oc-muted mt-1">{t('frameworks.subtitle')}</p>
      </header>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {frameworks?.map((fw) => (
          <Card key={fw.id} className="flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <h2 className="font-semibold text-ink">{fw.name}</h2>
                <p className="text-xs text-ink-muted mt-0.5">{fw.version}</p>
              </div>
              <Pill tone={fw.licensed ? 'good' : 'neutral'}>
                {fw.licensed ? t('frameworks.licensed') : t('frameworks.notLicensed')}
              </Pill>
            </div>

            <p className="text-sm text-ink-secondary leading-relaxed flex-1">{fw.description}</p>

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
              <span>{t('frameworks.questions', { count: fw.questionCount })}</span>
              {fw.licensed && fw.licenseExpiresAt && (
                <span>{t('frameworks.expiresOn', { date: formatDate(fw.licenseExpiresAt) })}</span>
              )}
            </div>

            {/* The citation is not decoration: it is how a user checks our
                reading of the law against the law itself. */}
            {fw.sourceCitation && (
              <p className="mt-3 pt-3 border-t border-line text-[11px] text-ink-muted leading-relaxed">
                <span className="font-medium">{t('frameworks.source')}: </span>
                {fw.sourceUrl ? (
                  <a
                    href={fw.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand hover:underline"
                  >
                    {fw.sourceCitation}
                  </a>
                ) : (
                  fw.sourceCitation
                )}
              </p>
            )}

            {!fw.licensed && allows('framework.purchase') && (
              <Button
                variant="primary"
                className="w-full mt-4"
                onClick={() => { setError(null); setPurchasing(fw); }}
              >
                {t('frameworks.purchase')} · {fw.licenseCost}
              </Button>
            )}
          </Card>
        ))}
      </div>

      <Modal
        open={purchasing !== null}
        onClose={() => setPurchasing(null)}
        title={t('frameworks.purchase')}
        footer={
          <>
            <Button onClick={() => setPurchasing(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={purchase.isPending}
              onClick={() => purchasing && purchase.mutate(purchasing.key)}
            >
              {purchase.isPending ? t('common.saving') : t('common.confirm')}
            </Button>
          </>
        }
      >
        {purchasing && (
          <div className="space-y-4">
            {error && <ErrorNotice message={error} />}
            <p className="text-ink">
              {t('frameworks.confirmPurchase', { name: purchasing.name })}
            </p>
            <p className="oc-muted">{t('frameworks.purchaseNote')}</p>
            <dl className="rounded-xl bg-surface-sunken p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink-secondary">{t('frameworks.cost', { count: purchasing.licenseCost })}</dt>
                <dd className="font-medium tabular-nums text-ink">{purchasing.licenseCost}</dd>
              </div>
              {pricing && (
                <>
                  <div className="flex justify-between">
                    <dt className="text-ink-secondary">{t('dashboard.credits')}</dt>
                    <dd className="font-medium tabular-nums text-ink">{pricing.balance}</dd>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-line">
                    <dt className="text-ink-secondary">
                      {t('frameworks.balanceAfter', { count: pricing.balance - purchasing.licenseCost })}
                    </dt>
                    <dd
                      className={
                        pricing.balance - purchasing.licenseCost < 0
                          ? 'font-medium tabular-nums text-critical'
                          : 'font-medium tabular-nums text-ink'
                      }
                    >
                      {pricing.balance - purchasing.licenseCost}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        )}
      </Modal>
    </div>
  );
}
